import React, { useContext, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ArrowUp, CornerDownLeft } from "lucide-react";
import { HRLocaleContext } from "../hr/i18n/HRLocaleProvider";
import LangSwitch from "../hr/components/LangSwitch";
import OnHumanHRLogo from "./logos/OnHumanHRLogo";
import NavigatorBackdrop from "./NavigatorBackdrop";
import { searchSite, AUTO_ROUTE } from "../assistant/siteSearch";

const copy = {
  en: {
    heading: "Welcome to OnHuman Navigator",
    description:
      "Start here: describe what you need and we’ll guide you or jump straight into the main site.",
    placeholder: "Please type your request…",
    main: "Go to main site",
    scopeNote: "I can only help with topics covered on this site. Try:",
    noResults: "No matching page found.",
    enterHint: "to open",
    send: "Search",
    doorsLead: "Or pick a starting point",
    doors: [
      { eyebrow: "For candidates", title: "I’m a candidate", href: "/adayhizmetlerim" },
      { eyebrow: "For employers", title: "I’m an employer", href: "/isverenhizmetlerim" },
    ],
  },
  tr: {
    heading: "OnHuman Navigator'a Hoş Geldiniz",
    description:
      "Buradan başlayın: ihtiyacınızı yazın, sizi yönlendirelim veya doğrudan ana sayfaya geçin.",
    placeholder: "İsteğinizi yazınız…",
    main: "Ana sayfaya git",
    scopeNote: "Sadece site ile ilgili konularda yardımcı olabilirim. Şunları deneyin:",
    noResults: "Eşleşen sayfa bulunamadı.",
    enterHint: "ile açın",
    send: "Ara",
    doorsLead: "Veya bir başlangıç noktası seçin",
    doors: [
      { eyebrow: "Adaylar için", title: "Adayım", href: "/adayhizmetlerim" },
      { eyebrow: "İşverenler için", title: "İşverenim", href: "/isverenhizmetlerim" },
    ],
  },
};

const exampleTags = {
  tr: [
    "Hizmetler hakkında bilgi almak istiyorum",
    "CV koçluğu istiyorum",
    "İşveren hizmetleri nelerdir?",
  ],
  en: [
    "Tell me about your services",
    "I want CV coaching",
    "What do you offer for employers?",
  ],
};

export default function Prompt({ mainHref = "/anamenu" }) {
  const { locale } = useContext(HRLocaleContext);
  const lang = locale === "en" ? "en" : "tr";
  const t = copy[lang];
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  // Search is synchronous and sub-millisecond, so results track every
  // keystroke — no submit required, no spinner, no model to wait on.
  const { results, confidence } = useMemo(() => searchSite(query, lang), [query, lang]);

  const hasQuery = query.trim().length > 0;
  const showResults = hasQuery && results.length > 0;
  const showEmpty = hasQuery && results.length === 0;

  const submit = (e) => {
    e?.preventDefault();
    const target = results[active] || results[0];
    if (target) navigate(target.href);
    else inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setQuery("");
      setActive(0);
    }
  };

  return (
    <div className="relative min-h-screen bg-paper font-hanken text-ink flex flex-col">
      <NavigatorBackdrop />

      <header className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 h-20 flex items-center justify-between gap-4">
          <Link to={mainHref} aria-label="OnHuman" className="shrink-0">
            <OnHumanHRLogo className="h-7 sm:h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4 sm:gap-6">
            <LangSwitch tone="dark" />
            <Link
              to={mainHref}
              className="group inline-flex items-center gap-2 font-semibold text-[14px] sm:text-[15px] text-navy hover:text-royal transition-colors duration-300"
            >
              {t.main}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-10 sm:py-14">
        <div className="w-full max-w-[680px] text-center">
          <h1 className="font-extrabold tracking-[-0.03em] leading-[1.08] text-[clamp(1.8rem,4vw,2.75rem)]">
            {t.heading}
          </h1>
          <p className="mt-4 text-[16px] sm:text-[17px] text-steel leading-relaxed max-w-xl mx-auto">
            {t.description}
          </p>

          {/* The composer: the page's centre of gravity. */}
          <div className="relative mt-8 sm:mt-10 text-left">
            <form onSubmit={submit}>
              <div className="flex items-center gap-2 bg-white border border-line rounded-2xl pl-5 pr-2 py-2 shadow-[0_6px_28px_-14px_rgba(17,28,48,0.28)] transition-colors duration-300 focus-within:border-royal">
                <Search className="h-5 w-5 text-steel/50 shrink-0" aria-hidden="true" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder={t.placeholder}
                  aria-label={t.placeholder}
                  role="combobox"
                  aria-expanded={showResults}
                  aria-controls="navigator-results"
                  autoComplete="off"
                  className="flex-1 min-w-0 bg-transparent py-3 text-[16px] sm:text-[17px] text-ink placeholder:text-steel/55 outline-none"
                />
                <button
                  type="submit"
                  aria-label={t.send}
                  className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-royal focus-visible:outline-offset-2 bg-royal text-white hover:bg-[#1A44C2] disabled:bg-platinum disabled:text-steel/50 disabled:hover:bg-platinum"
                  disabled={!showResults}
                >
                  <ArrowUp className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </form>

            {/* Results overlay the page rather than push it, so the layout
                stays still while typing. */}
            {showResults && (
              <ul
                id="navigator-results"
                role="listbox"
                className="absolute left-0 right-0 top-full mt-2 z-20 bg-white border border-line rounded-2xl overflow-hidden shadow-[0_16px_44px_-20px_rgba(17,28,48,0.35)] divide-y divide-line"
              >
                {results.map((r, i) => (
                  <li key={r.id}>
                    <Link
                      to={r.href}
                      role="option"
                      aria-selected={i === active}
                      onMouseEnter={() => setActive(i)}
                      className={`group relative flex items-center gap-4 px-5 py-3.5 transition-colors duration-200 ${
                        i === active ? "bg-royal/[0.07]" : "hover:bg-royal/[0.04]"
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute left-0 top-0 bottom-0 w-[3px] bg-royal transition-opacity duration-200 ${
                          i === active ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-[15px] text-ink">{r.title}</span>
                        <span className="block text-[13.5px] text-steel truncate">
                          {r.description}
                        </span>
                      </span>
                      {i === active && confidence >= AUTO_ROUTE && (
                        <span className="hidden sm:inline-flex items-center gap-1.5 text-[12px] font-semibold text-steel/70 shrink-0">
                          <CornerDownLeft className="h-3.5 w-3.5" aria-hidden="true" />
                          {t.enterHint}
                        </span>
                      )}
                      <ArrowRight className="h-4 w-4 text-steel/50 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {showEmpty && (
              <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-white border border-line rounded-2xl px-5 py-4 shadow-[0_16px_44px_-20px_rgba(17,28,48,0.35)]">
                <p className="text-[14.5px] font-semibold text-ink">{t.noResults}</p>
                <p className="mt-1 text-[13.5px] text-steel">{t.scopeNote}</p>
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {(exampleTags[lang] || []).map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setQuery(q);
                  setActive(0);
                  inputRef.current?.focus();
                }}
                className="rounded-full bg-white/80 border border-line px-3.5 py-1.5 text-[13px] text-steel backdrop-blur-sm transition-colors duration-300 hover:border-royal hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-royal focus-visible:outline-offset-2"
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        {/* Secondary path: the two audiences, kept compact. */}
        <div className="w-full max-w-[680px] mt-14 sm:mt-16">
          <div className="flex items-center gap-4">
            <span className="h-px flex-1 bg-line" aria-hidden="true" />
            <span className="font-semibold uppercase text-[11px] tracking-[0.13em] text-steel/70">
              {t.doorsLead}
            </span>
            <span className="h-px flex-1 bg-line" aria-hidden="true" />
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {t.doors.map((door) => (
              <Link
                key={door.href}
                to={door.href}
                className="group relative flex items-center justify-between gap-4 bg-white/90 border border-line rounded-xl px-5 py-4 backdrop-blur-sm transition-colors duration-300 hover:border-linestrong focus-visible:outline focus-visible:outline-2 focus-visible:outline-royal focus-visible:outline-offset-2"
              >
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl bg-royal origin-top scale-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100"
                />
                <span className="min-w-0">
                  <span className="block font-semibold uppercase text-[10.5px] tracking-[0.13em] text-royal">
                    {door.eyebrow}
                  </span>
                  <span className="block mt-1 font-extrabold text-[19px] tracking-[-0.01em] text-ink">
                    {door.title}
                  </span>
                </span>
                <ArrowRight className="h-[18px] w-[18px] text-steel/60 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-royal" />
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
